<template>
    <div>
        <div class="draggable"></div>

        <section class="demo">

            <section class="content fullscreen content-layer" :class="{'menuOpened': menuOpened}">
                <transition name="fade">
                    <template v-for="(presentation, index) in presentations">

                        <Presentation class="presentation fullscreen" 
                            v-if="index === navigationData.currentPresentationIndex"
                            :settings="settings"
                            :navigationData="navigationData"
                            :structure="structure"
                            :presentationIndex="index"
                            :presentation="presentations[index]"
                            :key="presentation.uri" />

                    </template>
                </transition>
            </section>

            <component 
                class="demo-overlay"
                :is="overlayComponent"
                v-if="overlayComponent" 
                :settings="settings"
                :navigationData="navigationData"
                :structure="structure" />

            <component 
                ref="menu"
                class="demo-menu"
                @menuOpened="menuOpened = $event"
                v-if="menuComponent" 
                :is="menuComponent"
                :settings="settings"
                :navigationData="navigationData"
                :structure="structure" />

            <video v-if="screenSaverActive"
                ref="screensaver"
                :src="currentScreenSaverVideo"
                :poster="currentScreenSaverPoster"
                class="screensaver-overlay fullscreen"
                :loop="false" preload="auto" muted
                @ended="onVideoEnded"></video>
            
        </section>
        <div class="transparent-overlay" v-if="portraitOverlay">
            <div class="overlay-inner">
                <div class="icon" @click.stop="closeOverlay()"><i class="material-icons md-24">close</i></div> 
                <img :src="require('@/demo/assets/images/portret_mobile_graphic.png')" alt="Portret mobile graphic">
                <h4>
                    Turn your phone sideways for the best viewing experience
                </h4>
            </div>
        </div>
    </div>
</template>

<script>

import structure from "@/demo/structure"
import { EventBus } from "@/core/utilities/event-bus.js"
import Presentation from '@/core/components/Presentation'
import _ from 'lodash'

export default {
    data: () => {
        return {
            portraitOverlay: true,
            screenSaverTimeout: false,
            screenSaverActive: false,
            screenSaverIndex: 0,
            menuOpened: false,
            demoScale: 0,
            structure: false,
            navigationData: false,
            linkList: [],
            originalSettings: {},
            requiredSettings: [
                'disableStructureDefaultsWarnings',
                'disableNavigationDebugLogging',
                'width',
                'height',
                'slideTransitionSpeed',
                'keyframeTransition',
                'bodyBackgroundColor',
                'demoBackgroundColor',
                'presentationBackgroundColor',
                'chapterBackgroundColor',
                'slideBackgroundColor',
                'keyframeBackgroundColor',
                'menuCloseOnNavigation',
                'menuPositionRight',
                'menuHideKeyframeProgress',
                'menuHideSlideProgress',
                'menuDark',
                'navigationBackEnterFirstKeyframe',
                'navigationBackEnterFirstSlide',
                'navigationCustomTitleBackKeyframe',
                'navigationCustomTitleBackSlide',
                'navigationCustomTitleBackChapter',
                'navigationCustomTitleNextKeyframe',
                'navigationCustomTitleNextSlide',
                'navigationCustomTitleNextChapter',
                'navigationButtonTitleRestart',
                'enableKeyboardNavigation',
                'enableKeyboardChapterUpDown',
                'enableMenuHotkeys',
                'showMenuHotkeys',
                'hotKeyNext',
                'hotKeyMenu',
                'hotKeyRestart',
                'screenSaver',
                'screenSaverEnabled',
                'screenSaverDelaySeconds',
                'screenSaverHotKey'
            ],
            hotKeysChapters: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
            hotKeysSlides: ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
            hotKeysPresentationLinks: ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l']
        }
    },
    watch: {
        $route(to, from) {
            this.onRouteUpdate(to, from)
        }
    },
    computed: {
        hasScreenSaver() {
            return this.settings.screenSaver && 
                this.settings.screenSaver.length && 
                this.settings.screenSaverDelaySeconds > 0 && 
                this.settings.screenSaverEnabled
        },
        currentScreenSaverVideo() {
            return this.settings.screenSaver[this.screenSaverIndex].src
        },
        currentScreenSaverPoster() {
            return this.settings.screenSaver[this.screenSaverIndex].poster
        },
        navigationStart() {
            return !this.navigationData.previousRoute.name
        },
        navigationEnd() {
            return !this.navigationData.nextRoute.name
        },
        presentations() {
            return this.structure.presentations
        },
        settings() {
            if (!this.structure.settings) return {}

            let settings = this.structure.settings
            let newSettings = {}
            try {
                newSettings = JSON.parse(localStorage.getItem('settings'))
            } catch (e) {
                console.warn(e)
                localStorage.clear()
            }
            return {...settings, ...newSettings}
        },
        menuComponent() {
            if (!this.structure.menuComponent) return false
            return this.structure.menuComponent
        },
        overlayComponent() {
            if (!this.structure.overlayComponent) return false
            return this.structure.overlayComponent
        }
    },
    methods: {
        closeOverlay(){
            this.portraitOverlay = false
        },
        onVideoEnded() {
            if (this.screenSaverIndex < this.settings.screenSaver.length - 1) {
                this.screenSaverIndex += 1
            } else {
                this.screenSaverIndex = 0
            }

            this.$nextTick(() => {
                this.$refs.screensaver.play()
            })
        },
        resetScreenSaver() {
            if (this.screenSaverTimeout) clearTimeout(this.screenSaverTimeout)
            this.screenSaverTimeout = false
            this.disableScreenSaver()

            if (!this.hasScreenSaver) return

            this.screenSaverTimeout = setTimeout(() => {
                this.activateScreenSaver()
            }, this.settings.screenSaverDelaySeconds * 1000)
        },
        activateScreenSaver() {
            if (this.screenSaverActive) return
            this.screenSaverActive = true
            this.screenSaverIndex = 0
            this.$nextTick(() => {
                this.$refs.screensaver.play()
            })
        },
        disableScreenSaver() {
            if (!this.screenSaverActive) return
            this.screenSaverActive = false
            console.log('disable screensaver')
        },
        onRouteUpdate(to, from = false) {
            this.updateRouteData(to)
            EventBus.$emit('navigationEvent', this.navigationData)
        },
        updateRouteData(to) {

            let overlay = false
            if (to.query.overlay) overlay = to.query.overlay

            let paused = false
            if (to.query.paused) paused = to.query.paused

            let presentationIndex = 0
            let chapterIndex = 0
            let slideIndex = 0
            let keyframeIndex = 0

            let presentation = false
            let chapter = false
            let slide = false
            let keyframe = false

            presentationIndex = this.getCurrentUriIndex('presentation', this.presentations, to.params.presentation)
            presentation = this.presentations[presentationIndex]

            if (presentation) {
                chapterIndex = this.getCurrentUriIndex('chapter', presentation.chapters, to.params.chapter)
                chapter = presentation.chapters[chapterIndex]
            }

            if (chapter) {
                slideIndex = this.getCurrentUriIndex('slide', chapter.slides, to.params.slide)
                slide = chapter.slides[slideIndex]
            }

            if (slide) {
                if (slide.keyframes.length) {
                    keyframeIndex = this.getCurrentUriIndex('keyframe', slide.keyframes, to.params.keyframe)
                } else {
                    keyframeIndex = -1
                }
            }

            let currentLinkIndex = this.linkList.findIndex(link => {
                return link.indexes[0] === presentationIndex &&
                    link.indexes[1] === chapterIndex &&
                    link.indexes[2] === slideIndex &&
                    link.indexes[3] === keyframeIndex   
            })

            let previousLink = false
            let currentLink = false
            let nextLink = false

            if (currentLinkIndex >= 0 && currentLinkIndex < this.linkList.length) {
                currentLink = this.linkList[currentLinkIndex]
            }
            
            if (currentLink && currentLinkIndex + 1 < this.linkList.length) {
                nextLink = this.linkList[currentLinkIndex + 1]
                if (currentLink.indexes[0] !== nextLink.indexes[0]) {
                    nextLink = false
                }
            }

            if (currentLink && currentLinkIndex > 0) {
                let back = 1
                previousLink = this.linkList[currentLinkIndex - back]

                
                if (this.settings.navigationBackEnterFirstKeyframe) {
                    while (previousLink) {
                        if (previousLink.indexes[3] < 1) { break }
                        back += 1
                        previousLink = this.linkList[currentLinkIndex - back]
                    }
                }
                
                if (this.settings.navigationBackEnterFirstSlide) {
                    while (previousLink) {
                        if (previousLink.indexes[2] <= currentLink.indexes[2] && currentLink.indexes[3] < 1) { break }
                        back += 1
                        previousLink = this.linkList[currentLinkIndex - back]
                    }
                }

                if (previousLink && currentLink.indexes[0] !== previousLink.indexes[0]) {
                    previousLink = false
                }
            }

            let previousRoute = {name: false, params: {} }
            let currentRoute = {name: false, params: {} }
            let nextRoute = {name: false, params: {} }


            if (currentLink.lastSlide && currentLink.lastKeyframe && chapter.nextChapterUri) {
                let nextChapterIndex = this.getCurrentUriIndex('chapter', presentation.chapters, chapter.nextChapterUri)
                nextLink = this.linkList.find(link => {
                    return link.indexes[0] === presentationIndex &&
                        link.indexes[1] === nextChapterIndex &&
                        link.indexes[2] === 0
                })         
            }

            if (currentLink.firstSlide && currentLink.firstKeyframe && chapter.prevChapterUri) {
                let prevChapterIndex = this.getCurrentUriIndex('chapter', presentation.chapters, chapter.prevChapterUri)
                previousLink = this.linkList.find(link => {
                    return link.indexes[0] === presentationIndex &&
                        link.indexes[1] === prevChapterIndex &&
                        link.indexes[2] === 0
                })
            }

            if (previousLink) previousRoute = previousLink.route
            if (currentLink) currentRoute = currentLink.route
            if (nextLink) nextRoute = nextLink.route



            let previousTitle = {name: false, params: {} }
            let currentTitle = {name: false, params: {} }
            let nextTitle = {name: false, params: {} }

            if (previousLink) previousTitle = previousLink.title
            if (currentLink) currentTitle = currentLink.title
            if (nextLink) nextTitle = nextLink.title


            let nextChapterLink = this.linkList.find(l => l.indexes[0] === currentLink.indexes[0] && l.indexes[1] === currentLink.indexes[1] + 1)
            let previousChapterLink = this.linkList.find(l => l.indexes[0] === currentLink.indexes[0] && l.indexes[1] === currentLink.indexes[1] - 1)
            




            this.navigationData = {
                paused,
                overlay,

                nextChapterLink,
                previousChapterLink,

                previousLink,
                currentLink,   
                nextLink,

                previousRoute,
                currentRoute,
                nextRoute,

                previousTitle,
                currentTitle,
                nextTitle,

                currentPresentationIndex: currentLink.indexes[0],
                currentChapterIndex: currentLink.indexes[1],
                currentSlideIndex: currentLink.indexes[2],
                currentKeyframeIndex: currentLink.indexes[3],

                currentPresentationUri: currentLink.route.params.presentation || presentation.uri || false,
                currentChapterUri: currentLink.route.params.chapter || chapter.uri || false,
                currentSlideUri: currentLink.route.params.slide || slide.uri || false,
                currentKeyframeUri: currentLink.route.params.keyframe || false
            }

        },
        getCurrentUriIndex(name, elements, uri) {
            let index = 0
            if (uri) {
                index = elements.findIndex(x => x.uri === uri)                
                if (index === -1) {
                    console.warn(`${name} with uri '${uri}' not found. Defaulting to first ${name}.`)
                    index = 0
                }
            }
            return index
        },
        updateDemoScale () {
            if (!this.structure) return
            const scaleX = window.innerWidth / this.structure.settings.width
            const scaleY = window.innerHeight / this.structure.settings.height
            this.demoScale = (scaleX > scaleY) ? scaleY : scaleX

            this.$el.querySelector('.demo').style.transform = 'translate(-50%, -50%) scale(' + this.demoScale + ')'
        },
        generateLinkList() {
            let list = []
            
            this.structure.presentations.forEach((presentation, presentationIndex) => {
                
                let firstPresentation = presentationIndex === 0
                let lastPresentation = presentationIndex === this.structure.presentations.length

                presentation.chapters.forEach((chapter, chapterIndex) => {

                    let firstChapter = chapterIndex === 0
                    let lastChapter = chapterIndex === presentation.chapters.length

                    chapter.slides.forEach((slide, slideIndex) => {
                        
                        let firstSlide = slideIndex === 0
                        let lastSlide = slideIndex === chapter.slides.length - 1

                        let baseObject = {
                            firstPresentation, lastPresentation, firstChapter, lastChapter, firstSlide, lastSlide, firstKeyframe: true, lastKeyframe: true,
                            indexes: [presentationIndex, chapterIndex, slideIndex, -1]
                        }

                        if (!slide.keyframes.length) {

                            if (firstPresentation && firstChapter && firstSlide) {
                                // root link
                                list.push({
                                    ...baseObject,
                                    title: "Home", 
                                    route: { name: 'root', params: {}}
                                })
                            }
                            else if (firstChapter && firstSlide) {
                                // presentation link
                                list.push({
                                    ...baseObject,
                                    title: presentation.title, 
                                    route: { name: 'presentation', params: {
                                        presentation: presentation.uri                                
                                    }
                                }})
                            } else if (firstSlide) {
                                // chapter link
                                list.push({
                                    ...baseObject,
                                    title: chapter.title, 
                                    route: { name: 'chapter', params: {
                                        presentation: presentation.uri,
                                        chapter: chapter.uri
                                    }
                                }})
                            } else { 
                                // slide link
                                list.push({
                                    ...baseObject,
                                    title: slide.title, 
                                    route: { name: 'slide', params: {
                                        presentation: presentation.uri,
                                        chapter: chapter.uri,
                                        slide: slide.uri
                                    }
                                }})
                            }

                        } else {

                            
                            slide.keyframes.forEach((keyframe, keyframeIndex) => {
                                
                                let firstKeyframe = keyframeIndex === 0
                                let lastKeyframe = keyframeIndex === slide.keyframes.length - 1
                                
                                baseObject.firstKeyframe = firstKeyframe
                                baseObject.lastKeyframe = lastKeyframe
                                baseObject.indexes = [presentationIndex, chapterIndex, slideIndex, keyframeIndex]

                                if (firstPresentation && firstChapter && firstSlide && firstKeyframe) {
                                    // root link
                                    list.push({
                                        ...baseObject,
                                        title: "Home", 
                                        route: { name: 'root', params: {}}
                                    })
                                }
                                else if (firstChapter && firstSlide && firstKeyframe) {
                                    // presentation link
                                    list.push({
                                        ...baseObject, 
                                        title: presentation.title || presentation.uri, 
                                        route: { name: 'presentation', params: {
                                            presentation: presentation.uri                                
                                        }
                                    }})
                                } else if (firstSlide && firstKeyframe) {
                                    // chapter link
                                    list.push({
                                        ...baseObject, 
                                        title: presentation.title || presentation.uri, 
                                        route: { name: 'chapter', params: {
                                            presentation: presentation.uri,
                                            chapter: chapter.uri                                
                                        }
                                    }})
                                } else if (firstKeyframe) { 
                                    // slide link
                                    list.push({
                                        ...baseObject, 
                                        title: presentation.title || presentation.uri, 
                                        route: { name: 'slide', params: {
                                            presentation: presentation.uri,
                                            chapter: chapter.uri,
                                            slide: slide.uri
                                        }
                                    }})
                                } else {
                                    // keyframe link
                                    list.push({
                                        ...baseObject, 
                                        title: presentation.title || presentation.uri, 
                                        route: { name: 'keyframe', params: {
                                            presentation: presentation.uri,
                                            chapter: chapter.uri,
                                            slide: slide.uri,
                                            keyframe: keyframe.uri
                                        }
                                    }})
                                }
                            })
                        }
                    })
                })
            })

            return list
        },
        parseStructure(settings) {
        
            if (!structure.settings) {
                console.error(`No structure settings found`) 
                return false
            } else {
                structure.settings = {...structure.settings, ...settings}

                this.requiredSettings.forEach(setting => {
                    if (structure.settings[setting] === undefined) {
                        console.error(`Missing demo setting: ${setting}`) 
                        return false
                    }
                })
            }

            if (!structure.menuComponent) console.error('No menu component found') 

            if (!structure.overlayComponent) {
                console.error(`No overlay component found`) 
                return false
            }

            if (!structure.presentations || !structure.presentations.length) {
                console.error(`No presentations found`) 
                return false
            }

            if (!structure.overlays) structure.overlays = []
            structure.overlays.forEach((overlay, overlayIndex) => {
                if (!overlay.uri) {
                    overlay.uri = `overlay-${overlayIndex}`
                    if (!structure.settings.disableStructureDefaultsWarnings) console.warn(`No uri defined for overlay: ${overlayIndex}. Defaulting to '${overlay.uri}'`)
                }
                if (!overlay.title) {
                    overlay.title = ''
                    if (!structure.settings.disableStructureDefaultsWarnings) console.warn(`No title defined for overlay: '${overlay.uri}'. Defaulting to empty string`)
                }
                if (!overlay.content) {
                    overlay.content = {}
                    if (!structure.settings.disableStructureDefaultsWarnings) console.warn(`No content defined for overlay: '${overlay.uri}'. Defaulting to empty object`)
                }
            })

            structure.presentations.forEach((presentation, presentationIndex) => {
                if (!presentation.chapters || !presentation.chapters.length){
                    console.error(`No chapters found in presentation: ${presentation.uri}`); 
                    return false
                }
                if (!presentation.uri) {
                    presentation.uri = `presentation-${presentationIndex}`
                    if (!structure.settings.disableStructureDefaultsWarnings) console.warn(`No uri defined for presentation: ${presentationIndex}. Defaulting to '${presentation.uri}'`)
                }
                if (!presentation.title) {
                    presentation.title = ''
                    if (!structure.settings.disableStructureDefaultsWarnings) console.warn(`No title defined for presentation: '${presentation.uri}'. Defaulting to empty string`)
                }
                if (!presentation.subtitle) {
                    presentation.subtitle = ''
                    if (!structure.settings.disableStructureDefaultsWarnings) console.warn(`No subtitle defined for presentation: '${presentation.uri}'. Defaulting to empty string`)
                }
                if (presentation.links) {
                    presentation.links.forEach((link, linkIndex) => {
                        if (!link.hotKey) link.hotKey = this.hotKeysPresentationLinks[linkIndex] || false
                    })
                } else {
                    presentation.links = []
                }
                    

                presentation.chapters.forEach((chapter, chapterIndex) => {
                    if (!chapter.slides || !chapter.slides.length) {
                        console.error(`No slides found in chapter: ${chapter.uri}`)
                        return false
                    }
                    if (chapter.hotKey === undefined) {
                        if (chapterIndex < this.hotKeysChapters.length) chapter.hotKey = this.hotKeysChapters[chapterIndex] || false
                    }
                    if (!chapter.uri) {
                        chapter.uri = `chapter-${chapterIndex}`
                        if (!structure.settings.disableStructureDefaultsWarnings) console.warn(`No uri defined for chapter: ${chapterIndex}. Defaulting to '${chapter.uri}'`)
                    }
                    if (!chapter.title) {
                        chapter.title = ''
                        if (!structure.settings.disableStructureDefaultsWarnings) console.warn(`No title defined for chapter: '${chapter.uri}'. Defaulting to empty string`)
                    }
                    if (!chapter.links) chapter.links = []


                    chapter.slides.forEach((slide, slideIndex) => {
                        if (!slide.uri) {
                            slide.uri = `slide-${slideIndex}`
                            if (!structure.settings.disableStructureDefaultsWarnings) console.warn(`No uri defined for slide: ${slideIndex}. Defaulting to '${slide.uri}'`)
                        }
                        if (slide.hotKey === undefined) {
                            if (slideIndex < this.hotKeysSlides.length) slide.hotKey = this.hotKeysSlides[slideIndex] || false
                        }
                        if (!slide.title) {
                            slide.title = ''
                            if (!structure.settings.disableStructureDefaultsWarnings) console.warn(`No title defined for slide: '${slide.uri}'. Defaulting to empty string`)
                        }
                        if (!slide.keyframes) slide.keyframes = []
                        if (!slide.links) slide.links = []


                        slide.keyframes.forEach((keyframe, keyframeIndex) => {
                            if (!keyframe.uri) {
                                keyframe.uri = `${keyframeIndex}`
                            }
                            if (!keyframe.title) {
                                keyframe.title = ''
                            }
                            if (!keyframe.links) keyframe.links = []
                        })
                    })
                })
            })

            return structure
        },
        updateSetting(data) {
            let settings = {}
            try {
                settings = JSON.parse(localStorage.getItem('settings'))
            } catch (e) {
                console.warn(e)
                localStorage.clear()
            }
            if (typeof settings !== 'object' || settings === null) {
                settings = {}
            }
            settings[data.key] = data.value
            localStorage.setItem('settings', JSON.stringify(settings))

            this.$set(this.structure.settings, data.key, data.value)
            this.setup(settings)
            this.resetScreenSaver()
        },
        resetSettings() {
            localStorage.clear()
            this.$set(this.structure, "settings", this.originalSettings)
            this.setup(this.originalSettings)
        },
        initializeDemo(settings) {
            this.originalSettings = { ...structure.settings }
            this.setup(settings)

            this.updateDemoScale()
            window.addEventListener('resize', _.debounce(this.updateDemoScale, 100))

            this.addKeyHandlers()
            
            document.body.addEventListener("mousedown", event => {
                this.resetScreenSaver()
            })

            this.resetScreenSaver()

            const route = this.$router.currentRoute
            this.updateRouteData(route)

            EventBus.$on('updateSetting', this.updateSetting)
            EventBus.$on('resetSettings', this.resetSettings)
        },
        setup(settings) {
            this.structure = this.parseStructure(settings)

            this.linkList = this.generateLinkList()

            let x = this.linkList.map(l => [l.route.name, l.route.params.presentation])

            this.$el.querySelector('.demo').style.width = this.structure.settings.width + 'px'
            this.$el.querySelector('.demo').style.height = this.structure.settings.height + 'px'
            this.$el.querySelector('.demo').style.backgroundColor = this.structure.settings.demoBackgroundColor;

            document.body.style.backgroundColor = this.structure.settings.bodyBackgroundColor;
        },
        addKeyHandlers() {
            document.body.addEventListener("keydown", event => {

                if (!this.screenSaverActive && this.settings.screenSaverHotKey && event.key === this.settings.screenSaverHotKey) {
                    this.activateScreenSaver()
                    if (this.screenSaverTimeout) clearTimeout(this.screenSaverTimeout)
                    this.screenSaverTimeout = false
                }  else {
                    this.resetScreenSaver()
                }
                
                if (!this.settings.enableKeyboardNavigation) return

                if (event.key === "ArrowRight" || event.key === "Right" || (this.settings.hotKeyNext && event.key === this.settings.hotKeyNext)) {

                    if (this.$router.currentRoute.query.pause) {
                        if (!this.navigationEnd) this.$router.push(this.navigationData.currentRoute).catch(err => {})
                    } else {
                        if (!this.navigationEnd) this.$router.push(this.navigationData.nextRoute).catch(err => {})
                    }

                    return
                }
                if (event.key === "ArrowLeft" || event.key === "Left") {
                    if (!this.navigationStart) this.$router.push(this.navigationData.previousRoute).catch(err => {})
                    return
                }

                if (this.settings.enableKeyboardChapterUpDown) {
                    if (event.key === "ArrowDown" || event.key === "Down") {
                        if (this.navigationData.nextChapterLink) {
                            this.$router.push({ name: "chapter", params: this.navigationData.nextChapterLink.route.params }).catch(err => {})
                            return
                        }
                    }
                    if (event.key === "ArrowUp" || event.key === "Up") {
                        if (this.navigationData.previousChapterLink) {
                            this.$router.push({ name: "chapter", params: this.navigationData.previousChapterLink.route.params }).catch(err => {})
                            return
                        }                      
                    }
                }

                if (this.settings.hotKeyRestart && event.key === this.settings.hotKeyRestart) {
                    if (!this.navigationStart) this.$router.push({name: 'root'}).catch(err => {})
                    if (this.$refs.menu) this.$refs.menu.closeMenu()
                    return
                }
                
            })
        }
    },
    components: {
        Presentation
    },
    mounted() {
        if (!structure) {
            console.error('No structure file found in demo folder')
            return
        }
        this.initializeDemo({})
    }
}
</script>

<style lang="scss">

    @import '~@/core/assets/scss/main.scss';

    .draggable {
        background: rgba(white, 0);
        height: 20px;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        z-index: 3;
        -webkit-app-region: drag;
        cursor: pointer;
    }
    
    .demo {
        position: absolute;
        transform-origin: center;
        top: 50%;
        left: 50%;
        overflow: hidden;

        .content {
            z-index: 0;
        }

        .demo-overlay {
            z-index: 1;
        }
        

        .demo-menu {
            z-index: 2;
            transform: translate3d(0, 0, 0);
        }

        .screensaver-overlay {
            z-index: 3;
        }
    }
    .transparent-overlay{
        position: fixed;
        display: none;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background: $color-nokia-blue-dark-transparent;
        .overlay-inner{
            background-color: white;
            border-radius: 10px;
            padding: 60px 30px 30px 30px;
            position: absolute;
            top: 50%;
            width: 80%;
            left: 50%;
            transform: translate(-50%, -50%);
            text-align: center;
            .icon{
                position: absolute;
                right: 25px;
                top: 25px;
                cursor: pointer;
                .material-icons{
                    font-size: 45px;
                }
            }
            img{
                display: block; 
                margin-left: auto; 
                margin-right: auto;
            }
        }
        h4{
            font-size: 18px;
            line-height: normal;
            text-align: left;
            margin-top: 25px;

        }
    }
    @media screen and (orientation:portrait) {
        // .demo{
        //     display: none;
        // }
        .transparent-overlay{
            display: block;
        }
    }

</style>